import { Dispatch, FC, Fragment, SetStateAction, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageType as ImageUploadType } from 'react-images-uploading';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { deleteMediaCache } from '@api/services/apimCdnPurge';
import { uploadImage } from '@api/services/imageManager';
import { Button, Switch } from '@components/base';
import { ImageCropAspectRatio } from '@enums/ImageCropAspectRatio';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import useSnack from '@hooks/useSnack';
import { ImageType, ImageTypes } from '@models/Image';
import { robustLazy } from '@utils/robustLazy';

const LazyImageCropper = robustLazy(() => import('./ImageCropper'));

export type UploadImageDialogProps = {
  hasUrgentPublishingOption?: boolean;
  image: ImageUploadType;
  loading: boolean;
  location?: string;
  onClose: VoidFunction;
  onSuccess?: (location: string, imageTypes?: ImageType[]) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
};

const UploadImageDialog: FC<UploadImageDialogProps> = ({
  hasUrgentPublishingOption,
  image,
  loading,
  location: initLocation,
  onClose,
  onSuccess,
  setLoading,
}) => {
  const { t } = useTranslation();

  const {
    defaultImageType,
    disabledImageTypes,
    handleUploadImage = uploadImage,
    refreshImages,
  } = useImageGroupData();

  const enabledImageTypes = ImageTypes.filter(imageType => !disabledImageTypes.includes(imageType));

  const [imageTypes, setImageTypes] = useState<ImageType[]>(
    defaultImageType ? [defaultImageType] : enabledImageTypes.length === 1 ? enabledImageTypes : []
  );

  const [croppedImage, setCroppedImage] = useState<Blob | File | undefined>(image.file);
  const [imageURL, setImageURL] = useState<string | undefined>(image.dataURL);
  const [isCropping, setIsCropping] = useState(false);
  const [uploadToNewLocation, setUploadToNewLocation] = useState(false);

  const { addSnack } = useSnack();

  const hasMultipleImageTypes = enabledImageTypes.length > 1;
  const showUrgentPublishingOption = Boolean(hasUrgentPublishingOption && initLocation);

  const handleUpload = () => {
    let imageId = initLocation?.split('/').at(-1);
    if (uploadToNewLocation) imageId = undefined;
    if (!croppedImage || imageTypes.length === 0) return;

    setLoading(true);

    handleUploadImage?.(imageTypes[0], croppedImage, imageTypes.slice(1), imageId)
      .then(async ({ data, ok, originalError }) => {
        if (originalError) throw originalError;
        if (!ok) throw new Error();
        if (imageId) await deleteMediaCache(imageId).catch(console.error);
        if (data) onSuccess?.(data.location, imageTypes);
        refreshImages(imageTypes);
        addSnack(t('image.upload.success'), { variant: 'success' });
        onClose();
      })
      .catch(err => {
        addSnack(err.message, { variant: 'error' });
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const onCrop = (canvas?: HTMLCanvasElement | null) => {
    if (canvas) {
      canvas.toBlob(blob => blob && setCroppedImage(blob));
      setImageURL(canvas.toDataURL());
    }

    setIsCropping(false);
  };

  return image && image.dataURL ? (
    <Fragment>
      <Dialog maxWidth='xl' onClose={loading ? undefined : onClose} open>
        <DialogTitle>
          <Stack direction='row' justifyContent='space-between' spacing={2}>
            <Typography variant='inherit'>{t('image.upload.label')}</Typography>

            <Button leftIcon='crop' onClick={() => setIsCropping(true)} variant='contained'>
              {t('crop')}
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Box display='flex' justifyContent='center' mb={2} mt={1}>
            <Box component='img' maxHeight={500} maxWidth={1} src={imageURL} />
          </Box>

          {(hasMultipleImageTypes || showUrgentPublishingOption) && (
            <Stack alignItems='flex-start' component={FormGroup}>
              {hasMultipleImageTypes && (
                <Fragment>
                  <FormLabel component='legend'>{t('images.upload.imageTypes')}</FormLabel>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabledImageTypes.every(imageType =>
                          imageTypes.includes(imageType)
                        )}
                        indeterminate={
                          enabledImageTypes.some(imageType => !imageTypes.includes(imageType)) &&
                          enabledImageTypes.some(imageType => imageTypes.includes(imageType))
                        }
                        onChange={({ target: { checked } }) =>
                          setImageTypes(checked ? enabledImageTypes : [])
                        }
                      />
                    }
                    label={t('all')}
                    value='all'
                  />

                  {enabledImageTypes.map(it => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={imageTypes.includes(it)}
                          onChange={({ target: { checked } }) =>
                            setImageTypes(prev =>
                              checked ? [...prev, it] : prev.filter(imageType => imageType !== it)
                            )
                          }
                        />
                      }
                      key={it}
                      label={t('imageType', { context: it })}
                      sx={{ ml: 1 }}
                      value={it}
                    />
                  ))}
                </Fragment>
              )}

              {showUrgentPublishingOption && (
                <Box p={1} pt={hasMultipleImageTypes ? 2 : 0} width={1}>
                  <Switch
                    checked={uploadToNewLocation}
                    helperText={t('images.upload.newLocation.description')}
                    label={t('images.upload.newLocation.label')}
                    onChange={(_, checked) => setUploadToNewLocation(checked)}
                  />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button disabled={loading} onClick={onClose}>
            {t('cancel')}
          </Button>

          <Tooltip title={imageTypes.length === 0 ? 'Select at least one image type' : undefined}>
            <span>
              <Button
                disabled={imageTypes.length === 0}
                loading={loading}
                onClick={handleUpload}
                variant='contained'
              >
                {t('upload')}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {isCropping && (
        <Suspense>
          <LazyImageCropper
            initCrop={
              imageTypes.length === 1 && imageTypes[0] !== defaultImageType
                ? (imageTypes[0] as ImageCropAspectRatio)
                : undefined
            }
            onCancel={() => setIsCropping(false)}
            onCrop={onCrop}
            src={image.dataURL}
          />
        </Suspense>
      )}
    </Fragment>
  ) : null;
};

export default UploadImageDialog;
